import React, { FC, ReactElement, useCallback, useRef, useState } from 'react'
import TVStreamTeaser from '@sport1/news-styleguide/TVStreamTeaser'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import { CallbackEvent, InplayerPaywallType } from '@/components/Paywall/types'
import useScript from '@/hooks/useScript'
import { TOP_DISPLAY_HEIGHT } from '@/utils/content/constants'
import { PaywallConfig } from '@/utils/paywall/paywallConfig'

type Props = {
    id: string
    brandingId: string
}

const Inplayer: FC<Props> = ({ id, brandingId }) => {
    const paywallRef = React.useRef<InplayerPaywallType | undefined>()
    const containerRef = React.useRef<(ReactElement & HTMLDivElement) | null>(null)
    const [hasAccess, setHasAccess] = React.useState<boolean | undefined>()
    const initial = useRef<boolean>(true)
    const [playerHeight, setPlayerHeight] = React.useState<number | undefined>()

    const [inplayerLoaded, setInplayerLoaded] = useState(
        useScript('https://assets.inplayer.com/paywall/v3/paywall.min.js', () =>
            setInplayerLoaded(true)
        )
    )

    const handlePaywallInit = React.useCallback(() => {
        paywallRef.current = new window.InplayerPaywall(
            PaywallConfig.merchantUUID,
            [
                {
                    id,
                    options: {
                        brandingId,
                        noPreview: true,
                    },
                },
            ],
            {
                oauthAppKey: PaywallConfig.oauthAppKey,
                language: PaywallConfig.language,
                hideUserMenu: true,
            }
        )
        paywallRef.current.setLanguage(PaywallConfig.language)
        paywallRef.current.on('access', ({ data }: CallbackEvent) => setHasAccess(data?.hasAccess))
        paywallRef.current.on('logout', () => setHasAccess(false))
    }, [id, brandingId])

    const setNewPlayerHeight = useCallback(() => {
        const playerWidth = containerRef.current?.getBoundingClientRect().width || 0
        const newPlayerHeight = playerWidth / (16 / 9)
        if (playerHeight !== newPlayerHeight) {
            setPlayerHeight(Math.min(newPlayerHeight, TOP_DISPLAY_HEIGHT))
        }
    }, [playerHeight])

    const showAccountDialog = useCallback(() => {
        if (paywallRef.current?.showPaywall) {
            paywallRef.current.showPaywall({
                asset: {
                    assetId: id,
                },
                registerFirst: true,
            })
        }
    }, [id])

    React.useEffect(() => {
        if (initial.current && inplayerLoaded) {
            initial.current = false
            handlePaywallInit()
            setNewPlayerHeight()
            window.addEventListener('resize', setNewPlayerHeight)
        }
    }, [handlePaywallInit, inplayerLoaded, playerHeight, setNewPlayerHeight])

    return (
        <NonFlexingContainer innerRef={containerRef} height={playerHeight} position="relative">
            <div
                id={`inplayer-${id}`}
                className={!hasAccess ? 'inplayer-livestream hidden' : undefined}
            ></div>
            <style jsx global>{`
                #inplayer-${id} iframe {
                    height: ${playerHeight}px;
                }
            `}</style>
            {hasAccess !== undefined && !hasAccess ? (
                <NonFlexingContainer
                    height={playerHeight}
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                >
                    <TVStreamTeaser
                        maxHeight={playerHeight}
                        logoType="sport1plus"
                        headlineText="SPORT1+"
                        subHeadlineText="Internationaler Spitzensport im Livestream auf Deinem PC, Smartphone und Tablet. Jetzt hier im Jahresabo für nur 10€ im Monat abnonnieren."
                        buttonText="Abonnieren"
                        mainImageUrl="https://reshape.sport1.de/c/t/7EC3DE94-E9B8-44B1-AE7A-45455677D150/0x0"
                        mainImageAlt="Internationaler Spitzensport im Livestream auf Deinem PC, Smartphone und Tablet. Jetzt hier im Jahresabo für nur 10€ im Monat abnonnieren."
                        onPress={showAccountDialog}
                        testID="tv-stream-teaser"
                    />
                </NonFlexingContainer>
            ) : null}
        </NonFlexingContainer>
    )
}

export default Inplayer
